import { useEffect, useMemo, useState } from 'react';
import {
  Search,
  Grid3X3,
  List,
  Plus,
  Eye,
  ShoppingCart,
  TrendingUp,
  ChevronRight,
  X,
  BarChart3
} from 'lucide-react';
import dayjs from 'dayjs';
import { api } from '@/lib/api';
import { formatCurrency, formatNumber, formatPercent, pad2 } from '@/lib/utils';
import type { LiveDataEntity, PlanningEntity } from '@shared/types';

export default function LiveDataListPage() {
  const [liveData, setLiveData] = useState<LiveDataEntity[]>([]);
  const [plannings, setPlannings] = useState<PlanningEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('list');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selected, setSelected] = useState<LiveDataEntity | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.getLiveData(), api.getPlannings()])
      .then(([ld, pl]) => {
        setLiveData(ld);
        setPlannings(pl);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    let list = liveData;
    if (keyword) {
      const k = keyword.toLowerCase();
      list = list.filter(
        (l) => l.title.toLowerCase().includes(k) || l.hostName.toLowerCase().includes(k)
      );
    }
    if (statusFilter !== 'all') {
      const today = dayjs().format('YYYY-MM-DD');
      const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      const weekAgo = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      list = list.filter((l) => {
        if (statusFilter === 'today') return l.date === today;
        if (statusFilter === 'yesterday') return l.date === yesterday;
        if (statusFilter === 'week') return l.date >= weekAgo;
        return true;
      });
    }
    return list;
  }, [liveData, keyword, statusFilter]);

  return (
    <div>
      <header className="border-b-2 border-border pb-8 mb-8">
        <div className="text-xs uppercase tracking-widest text-primary font-space mb-2">
          LIVE DATA / {dayjs().format('YYYY.MM.DD')}
        </div>
        <h1 className="text-[clamp(3rem,12vw,8rem)] font-bold leading-[0.85] tracking-tighter uppercase font-space">
          直播<br />数据<span className="text-primary">.</span>
        </h1>
      </header>

      {/* 工具栏 */}
      <div className="border-2 border-border bg-background p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] flex items-center gap-2 border-2 border-border px-3 py-2 focus-within:border-primary">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索场次 / 主播"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <div className="flex border-2 border-border">
          {[
            { id: 'all', label: '全部' },
            { id: 'today', label: '今日' },
            { id: 'yesterday', label: '昨日' },
            { id: 'week', label: '近 7 天' }
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setStatusFilter(s.id)}
              className={`px-4 py-2 text-sm font-bold uppercase tracking-widest border-r-2 last:border-r-0 border-border transition-colors ${
                statusFilter === s.id ? 'bg-primary text-black' : 'hover:bg-accent'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex border-2 border-border">
          <button
            onClick={() => setView('list')}
            className={`px-3 py-2 border-r-2 border-border ${view === 'list' ? 'bg-primary text-black' : 'hover:bg-accent'}`}
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView('grid')}
            className={`px-3 py-2 ${view === 'grid' ? 'bg-primary text-black' : 'hover:bg-accent'}`}
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-primary text-black px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-white"
        >
          <Plus className="w-4 h-4" strokeWidth={3} />
          录入场次
        </button>
      </div>

      {/* 列表 / 网格 */}
      {loading ? (
        <div className="text-foreground font-space uppercase tracking-widest text-sm py-32 text-center">
          LOADING<span className="text-primary">_</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-border p-16 text-center text-muted-foreground uppercase tracking-widest">
          暂无数据
        </div>
      ) : view === 'list' ? (
        <ListView items={filtered} onSelect={setSelected} />
      ) : (
        <GridView items={filtered} onSelect={setSelected} />
      )}

      {/* 详情弹窗 */}
      {selected && <DetailModal item={selected} onClose={() => setSelected(null)} />}

      {/* 新增弹窗 */}
      {showAdd && (
        <AddLiveDataModal
          plannings={plannings}
          onClose={() => setShowAdd(false)}
          onSubmit={async (data) => {
            await api.createLiveData(data);
            setShowAdd(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function ListView({ items, onSelect }: { items: LiveDataEntity[]; onSelect: (l: LiveDataEntity) => void }) {
  return (
    <div className="border-2 border-border overflow-x-auto">
      <table className="w-full">
        <thead className="bg-accent border-b-2 border-border">
          <tr className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
            <th className="px-4 py-3 text-left">#</th>
            <th className="px-4 py-3 text-left">场次名称</th>
            <th className="px-4 py-3 text-left">主播</th>
            <th className="px-4 py-3 text-left">日期</th>
            <th className="px-4 py-3 text-right">观看</th>
            <th className="px-4 py-3 text-right">GMV</th>
            <th className="px-4 py-3 text-right">转化率</th>
            <th className="px-4 py-3 text-right">互动率</th>
            <th className="px-4 py-3 text-right">详情</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr
              key={item.id}
              className="border-b-2 border-border hover:bg-primary hover:text-black cursor-pointer transition-colors duration-0"
              onClick={() => onSelect(item)}
            >
              <td className="px-4 py-3 font-space font-bold tabular-nums">{pad2(i + 1)}</td>
              <td className="px-4 py-3 font-bold">{item.title}</td>
              <td className="px-4 py-3">{item.hostName}</td>
              <td className="px-4 py-3 font-space text-sm">{item.date}</td>
              <td className="px-4 py-3 text-right tabular-nums font-space">{formatNumber(item.viewCount)}</td>
              <td className="px-4 py-3 text-right tabular-nums font-space font-bold">{formatCurrency(item.totalGmv)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatPercent(item.viewToConversionRate)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatPercent(item.viewToInteractionRate)}</td>
              <td className="px-4 py-3 text-right">
                <ChevronRight className="w-4 h-4 inline" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GridView({ items, onSelect }: { items: LiveDataEntity[]; onSelect: (l: LiveDataEntity) => void }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-border">
      {items.map((item, i) => (
        <div
          key={item.id}
          className="border-r-2 border-b-2 last:border-r-0 border-border p-6 hover:bg-primary hover:text-black transition-colors duration-200 cursor-pointer"
          onClick={() => onSelect(item)}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="font-space font-bold text-2xl tabular-nums opacity-50">{pad2(i + 1)}</span>
            <span className="text-xs font-space uppercase tracking-widest">{item.date}</span>
          </div>
          <div className="font-bold uppercase tracking-tight text-lg mb-1">{item.title}</div>
          <div className="text-sm opacity-70 mb-4">{item.hostName} · {item.duration} 分钟</div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="border-2 border-current p-2">
              <div className="text-xs uppercase tracking-widest opacity-70">GMV</div>
              <div className="font-bold font-space tabular-nums">{formatCurrency(item.totalGmv)}</div>
            </div>
            <div className="border-2 border-current p-2">
              <div className="text-xs uppercase tracking-widest opacity-70">观看</div>
              <div className="font-bold font-space tabular-nums">{formatNumber(item.viewCount)}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailModal({ item, onClose }: { item: LiveDataEntity; onClose: () => void }) {
  const goToReview = async () => {
    const review = await api.getLiveReviewByLiveData(item.id).catch(() => null);
    if (review) {
      window.location.hash = `/review/${review.id}`;
    } else {
      const newReview = await api.createReviewFromLiveData(item.id);
      window.location.hash = `/review/${newReview.id}`;
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-background border-2 border-border w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b-2 border-border p-6">
          <h2 className="text-2xl font-bold uppercase font-space">{item.title}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          <div className="text-sm text-muted-foreground mb-4 font-space uppercase tracking-widest">
            {item.hostName} · {item.date} · {item.duration} 分钟
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-2 border-border">
            {[
              { label: 'GMV', value: formatCurrency(item.totalGmv) },
              { label: '观看人数', value: formatNumber(item.viewCount) },
              { label: '订单数', value: formatNumber(item.transactionCount) },
              { label: '转化率', value: formatPercent(item.viewToConversionRate) },
              { label: '互动率', value: formatPercent(item.viewToInteractionRate) },
              { label: '新增粉丝', value: formatNumber(item.newFollowers) },
              { label: '平均观看时长', value: `${item.avgWatchDuration}s` },
              { label: '曝光-观看率', value: formatPercent(item.exposureToViewRate) },
              { label: '千次曝光成交', value: formatCurrency(item.gmvPer1000Views) }
            ].map((m, i) => (
              <div key={i} className="border-r-2 border-b-2 last:border-r-0 border-border p-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{m.label}</div>
                <div className="font-bold font-space tabular-nums text-lg mt-1">{m.value}</div>
              </div>
            ))}
          </div>

          <div className="border-2 border-border p-4 mt-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">流量来源</div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: '付费广告', value: item.adsTraffic, color: 'bg-primary' },
                { label: '推荐流量', value: item.boostTraffic, color: 'bg-info' },
                { label: '自然流量', value: item.organicTraffic, color: 'bg-foreground' },
                { label: '公域流量', value: item.publicTraffic, color: 'bg-muted-foreground' }
              ].map((t) => (
                <div key={t.label} className="border-2 border-border p-2">
                  <div className="text-xs text-muted-foreground">{t.label}</div>
                  <div className="font-bold font-space tabular-nums">{formatNumber(t.value)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t-2 border-border p-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="border-2 border-border px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-accent"
          >
            关闭
          </button>
          <button
            onClick={goToReview}
            className="bg-primary text-black px-6 py-3 font-bold uppercase tracking-widest text-sm flex items-center gap-2 hover:bg-white"
          >
            <BarChart3 className="w-4 h-4" /> 进入复盘
          </button>
        </div>
      </div>
    </div>
  );
}

function AddLiveDataModal({
  plannings,
  onClose,
  onSubmit
}: {
  plannings: PlanningEntity[];
  onClose: () => void;
  onSubmit: (data: Partial<LiveDataEntity>) => Promise<void>;
}) {
  const [form, setForm] = useState({
    title: '',
    hostName: '',
    date: dayjs().format('YYYY-MM-DD'),
    duration: 120,
    totalGmv: 100000,
    viewCount: 10000,
    transactionCount: 200,
    viewToConversionRate: 3.5,
    viewToInteractionRate: 4.2,
    planningId: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      alert('请填写场次名称');
      return;
    }
    setSubmitting(true);
    try {
      const total = Number(form.viewCount);
      const payload: Partial<LiveDataEntity> = {
        ...form,
        adsTraffic: Math.round(total * 0.25),
        boostTraffic: Math.round(total * 0.1),
        organicTraffic: Math.round(total * 0.35),
        publicTraffic: Math.round(total * 0.3),
        buyerCount: Number(form.transactionCount),
        avgWatchDuration: 90,
        avgOnlineCount: Math.round(total / 10),
        exposureToViewRate: 12,
        newFollowers: Math.round(total * 0.05),
        productClickToConversionRate: 6,
        exposureToViewCountRate: 22,
        gmvPer1000Views: Math.round((Number(form.totalGmv) / total) * 1000),
        newFollows: Math.round(total * 0.05),
        likes: 5000,
        comments: 800,
        shares: 200
      };
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-background border-2 border-border w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b-2 border-border p-6">
          <h2 className="text-2xl font-bold uppercase font-space">录入直播数据</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {plannings.length > 0 && (
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">关联策划(可选)</label>
              <select
                value={form.planningId}
                onChange={(e) => setForm({ ...form, planningId: e.target.value })}
                className="w-full bg-input border-2 border-border px-3 py-3 outline-none focus:border-primary"
              >
                <option value="">不关联</option>
                {plannings.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} - {p.hostName}</option>
                ))}
              </select>
            </div>
          )}
          {[
            { key: 'title', label: '场次名称 *', type: 'text' },
            { key: 'hostName', label: '主播', type: 'text' },
            { key: 'date', label: '日期', type: 'date' },
            { key: 'duration', label: '时长 (分钟)', type: 'number' },
            { key: 'totalGmv', label: 'GMV (元)', type: 'number' },
            { key: 'viewCount', label: '观看人数', type: 'number' },
            { key: 'transactionCount', label: '订单数', type: 'number' },
            { key: 'viewToConversionRate', label: '转化率 (%)', type: 'number' },
            { key: 'viewToInteractionRate', label: '互动率 (%)', type: 'number' }
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                {f.label}
              </label>
              <input
                type={f.type}
                value={(form as any)[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value })}
                className="w-full bg-input border-2 border-border px-4 py-3 outline-none focus:border-primary font-space"
              />
            </div>
          ))}
        </div>
        <div className="border-t-2 border-border p-6 flex justify-end gap-3">
          <button onClick={onClose} className="border-2 border-border px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-accent">
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-primary text-black px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-white disabled:opacity-50"
          >
            {submitting ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
